import React from 'react';
import { Persistor } from 'redux-persist';
import { useEffect, useState } from 'react';
import { PersistGate } from 'redux-persist/integration/react';

// Next.js sends back empty html from the server
// SSR should not depend on Redux Persist - if it does nothing will be prerendered
// https://github.com/vercel/next.js/discussions/16389#discussioncomment-469660

type Props = {
	children: React.ReactNode;
	loading?: boolean;
	persistor: Persistor;
};

/**
 * SSR aware persist gate
 * @param props Same as the original PersistGate
 * @returns persisted or un-persisted component
 */

export const PersistGateSSR: React.FC<Props> = ({ children, loading, persistor }) => {
	const [isClient, setIsClient] = useState(false);

	// Detect when the component is running on the client
	useEffect(() => {
		setIsClient(true);
	}, []);

	// IMPORTANT: Render children on server for SSR, wrap with PersistGate on client for hydration
	if (!isClient) {
		// Render children on server side for SSR/SEO - Redux will hydrate on client
		return <>{children}</>;
	}

	return (
		// suppressHydrationWarning needed because server renders without Redux, client with Redux
		// https://blog.hao.dev/render-client-side-only-component-in-next-js
		// https://reactjs.org/docs/react-dom.html#hydrate
		<div suppressHydrationWarning={true}>
			<PersistGate loading={loading} persistor={persistor}>
				{children}
			</PersistGate>
		</div>
	);
};
